import { memo, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import APIconButton from '@shared/components/atoms/icon-button';
import APTypography from '@shared/components/atoms/typography';
import { useTranslate } from '@shared/hooks/use-translate';

import type { IAPSideSheetProps } from './types';
import type { CSSProperties } from 'react';

import './styles.scss';

/**
 * APSideSheet
 *
 * Structural side panel that slides over the page.
 * The component is intentionally lean: it only provides layout,
 * spacing, and close handling so teams can inject any content.
 */
export const APSideSheet = memo<IAPSideSheetProps>(
  ({
    isOpen,
    onClose,
    title,
    subtitle,
    headerSlot,
    actions,
    footer,
    slots,
    content,
    children,
    side = 'right',
    width = 480,
    showCloseButton = true,
    showBackdrop = true,
    closeOnBackdropClick = true,
    className,
    ariaLabel,
    ariaDescribedBy,
    'data-testid': testId = 'ap-side-sheet',
  }) => {
    const translate = useTranslate();
    const ANIMATION_DURATION_MS = 300;
    const [isVisible, setIsVisible] = useState(isOpen);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimerRef = useRef<number | null>(null);

    // Manage mount/unmount to allow exit animation
    useEffect(() => {
      if (isOpen) {
        if (closeTimerRef.current) {
          window.clearTimeout(closeTimerRef.current);
          closeTimerRef.current = null;
        }
        setIsVisible(true);
        setIsClosing(false);
      } else if (isVisible) {
        setIsClosing(true);
        closeTimerRef.current = window.setTimeout(() => {
          setIsVisible(false);
          setIsClosing(false);
          closeTimerRef.current = null;
        }, ANIMATION_DURATION_MS);
      }

      return () => {
        if (closeTimerRef.current) {
          window.clearTimeout(closeTimerRef.current);
        }
      };
    }, [isOpen, isVisible]);

    // Lock body scroll while open or animating out
    useEffect(() => {
      if (!isVisible || (!isOpen && !isClosing)) return;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }, [isClosing, isOpen, isVisible]);

    // Close on ESC key
    useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const sheetClasses = useMemo(
      () =>
        clsx(
          'ap-side-sheet',
          `ap-side-sheet--${side}`,
          isOpen && !isClosing && 'ap-side-sheet--open',
          isClosing && 'ap-side-sheet--closing',
          className
        ),
      [className, isClosing, isOpen, side]
    );

    if (!isVisible) return null;

    const panelStyle: CSSProperties =
      typeof width === 'number' ? { width: `${width}px` } : { width };

    const handleBackdropClick = () => {
      if (!closeOnBackdropClick || isClosing) return;
      onClose();
    };

    const bodyContent = slots?.content ?? content ?? children;
    const footerContent = slots?.footer ?? footer;

    const defaultHeader = (
      <header className="ap-side-sheet__header">
        <div className="ap-side-sheet__title-group">
          <div className="ap-side-sheet__titles">
            {title ? (
              <APTypography as="h2" size="large" weight="600" className="ap-side-sheet__title">
                {title}
              </APTypography>
            ) : null}
            {subtitle ? (
              <APTypography size="small" color="mutedSecondary" className="ap-side-sheet__subtitle">
                {subtitle}
              </APTypography>
            ) : null}
          </div>
          {headerSlot ? (
            <div className="ap-side-sheet__header-slot" data-testid={`${testId}-header-slot`}>
              {headerSlot}
            </div>
          ) : null}
        </div>
        <div className="ap-side-sheet__header-actions">
          {actions}
          {showCloseButton ? (
            <APIconButton
              icon="close_line"
              variant="transparent"
              size="small"
              aria-label={translate('aria.close-side-sheet')}
              onClick={() => {
                if (!isClosing) onClose();
              }}
            />
          ) : null}
        </div>
      </header>
    );

    const headerContent = slots?.header ?? defaultHeader;

    return (
      <div
        className={sheetClasses}
        data-testid={testId}
        aria-modal="true"
        role="dialog"
        aria-label={ariaLabel || title || 'Side sheet'}
        aria-describedby={ariaDescribedBy}
      >
        {showBackdrop ? (
          <div
            className="ap-side-sheet__backdrop"
            onClick={handleBackdropClick}
            role="presentation"
          />
        ) : null}
        <aside className="ap-side-sheet__panel" style={panelStyle}>
          {headerContent}

          <div className="ap-side-sheet__body">{bodyContent}</div>

          {footerContent ? (
            <footer className="ap-side-sheet__footer">{footerContent}</footer>
          ) : null}
        </aside>
      </div>
    );
  }
);

APSideSheet.displayName = 'APSideSheet';

export type { IAPSideSheetProps } from './types';
export default APSideSheet;
